import React, { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import XDesignDashboard from './components/XDesignDashboard';
import Login from './components/Login';
import WearCatalogue from './components/WearCatalogue';
import {
  PhotoIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
  SunIcon,
  MoonIcon,
  ShoppingCartIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  StarIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

type ViewType = 'generations' | 'paid-users-generations' | 'subscriptions' | 'orders' | 'users' | 'credits' | 'wear-catalogue' | 'settings';

interface SidebarItem {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  view: ViewType;
}

function Dashboard(): JSX.Element {
  const { currentUser, logout, isAuthorized } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<ViewType>('generations');

  const sidebarItems: SidebarItem[] = [
    { name: 'Generations', icon: PhotoIcon, view: 'generations' },
    { name: 'Paid Users Generations', icon: StarIcon, view: 'paid-users-generations' },
    { name: 'Subscriptions', icon: CreditCardIcon, view: 'subscriptions' },
    { name: 'Orders', icon: ShoppingCartIcon, view: 'orders' },
    { name: 'Users', icon: UserGroupIcon, view: 'users' },
    { name: 'Credits', icon: CurrencyDollarIcon, view: 'credits' },
    { name: 'Wear Catalogue', icon: Squares2X2Icon, view: 'wear-catalogue' },
    { name: 'Settings', icon: Cog6ToothIcon, view: 'settings' },
  ];

  if (!currentUser || !isAuthorized) {
    return <Login />;
  }

  return (
    <div className="app-bg font-sans flex h-screen overflow-hidden text-slate-900 dark:text-slate-100">
      {/* Mobile Header with Hamburger */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/85 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/60 dark:border-white/10 z-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <div className="h-9 w-9 rounded-xl bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30 mr-2.5">
            <PhotoIcon className="h-5 w-5 text-white" />
          </div>
          <span className="text-lg font-bold tracking-tight">XDesign AI</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors focus:outline-none"
          >
            {theme === 'light' ? (
              <MoonIcon className="h-5 w-5" />
            ) : (
              <SunIcon className="h-5 w-5" />
            )}
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`sidebar flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200/70 dark:border-white/10 transition-all duration-300
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        ${sidebarOpen ? 'md:w-64' : 'md:w-[76px]'}
        fixed h-full z-50 md:z-30`}
      >
        {/* Brand */}
        <div className={`flex items-center shrink-0 border-b border-slate-200/70 dark:border-white/10 ${sidebarOpen ? 'justify-between h-16 px-4' : 'h-16 flex-col justify-center gap-3'}`}>
          <div className={`flex items-center ${sidebarOpen ? '' : 'md:justify-center md:w-full'}`}>
            <div className="h-9 w-9 shrink-0 rounded-lg bg-orange-500 flex items-center justify-center">
              <PhotoIcon className="h-5 w-5 text-white" />
            </div>
            <span className={`ml-3 text-base font-semibold tracking-tight text-slate-900 dark:text-white whitespace-nowrap transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'hidden md:block md:opacity-0'}`}>
              XDesign AI
            </span>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? 'Collapse' : 'Expand'}
            className="hidden md:flex p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarOpen ? "M11 19l-7-7 7-7M13 5l7 7-7 7" : "M13 5l7 7-7 7M5 5l7 7-7 7"} />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-3">
          <p className={`px-3 pb-2 text-xs font-medium text-slate-400 dark:text-slate-500 ${sidebarOpen ? 'block' : 'hidden md:block'}`}>
            Overview
          </p>
          <div className="space-y-1">
            {sidebarItems.map((item) => {
              const isActive = currentView === item.view;
              const icon = <item.icon className={`h-5 w-5 shrink-0 transition-colors ${isActive ? 'text-orange-600 dark:text-orange-400' : 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300'}`} />;
              return (
                <a
                  key={item.name}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentView(item.view);
                    setMobileMenuOpen(false);
                  }}
                  className={`group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium cursor-pointer transition-colors
                    ${isActive
                      ? 'bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white'}`}
                >
                  <span className={sidebarOpen ? '' : 'md:flex md:w-full md:justify-center'}>
                    {icon}
                  </span>
                  <span className={`whitespace-nowrap transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'hidden md:block md:opacity-0'}`}>
                    {item.name}
                  </span>
                </a>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="px-3 py-4 border-t border-slate-200/70 dark:border-white/10 space-y-2">
          <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
            {currentUser?.photoURL ? (
              <img
                className="h-9 w-9 shrink-0 rounded-full object-cover ring-2 ring-orange-500/30"
                src={currentUser.photoURL}
                alt="User"
              />
            ) : (
              <div className="h-9 w-9 shrink-0 rounded-full bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center">
                <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">
                  {currentUser?.displayName?.[0] || 'U'}
                </span>
              </div>
            )}
            <div className={`min-w-0 transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'hidden md:block md:opacity-0'}`}>
              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                {currentUser?.displayName || 'User'}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Admin</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white transition-colors group"
          >
            <span className={sidebarOpen ? '' : 'md:flex md:w-full md:justify-center'}>
              <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0 text-slate-400 group-hover:text-orange-500 dark:text-slate-500 dark:group-hover:text-orange-400 transition-colors" />
            </span>
            <span className={`whitespace-nowrap transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'hidden md:block md:opacity-0'}`}>
              Sign out
            </span>
          </button>
          <div className={`px-3 pt-1 transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'hidden md:block md:opacity-0'}`}>
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors"
            >
              {theme === 'light' ? (
                <MoonIcon className="h-5 w-5" />
              ) : (
                <SunIcon className="h-5 w-5" />
              )}
              {theme === 'light' ? 'Dark mode' : 'Light mode'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`relative flex-1 ${sidebarOpen ? 'md:ml-64' : 'md:ml-[76px]'} h-screen overflow-hidden pt-16 md:pt-0 transition-all duration-300`}>
        {currentView === 'generations' && <XDesignDashboard activeTab="generations" />}
        {currentView === 'paid-users-generations' && <XDesignDashboard activeTab="paid-users-generations" />}
        {currentView === 'subscriptions' && <XDesignDashboard activeTab="subscriptions" />}
        {currentView === 'orders' && <XDesignDashboard activeTab="orders" />}
        {currentView === 'users' && <XDesignDashboard activeTab="users" />}
        {currentView === 'credits' && <XDesignDashboard activeTab="credits" />}
        {currentView === 'wear-catalogue' && <WearCatalogue />}
        {currentView === 'settings' && <XDesignDashboard activeTab="settings" />}
      </div>
    </div>
  );
}

function App(): JSX.Element {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Dashboard />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;